// packages/analytics/src/sanitize.ts
const DYNAMIC = /^:[a-zA-Z][a-zA-Z0-9]*$/

export interface ReplayPolicyForApp {
  readonly recordable: readonly string[]
  readonly excluded: readonly string[]
}

export function flattenPatterns(policyForApp: ReplayPolicyForApp): string[] {
  return [...policyForApp.recordable, ...policyForApp.excluded]
}

function matches(pathname: string, pattern: string): boolean {
  const p = pathname.split('/').filter(Boolean)
  const q = pattern.split('/').filter(Boolean)
  if (p.length !== q.length) return false
  return q.every((seg, i) => (DYNAMIC.test(seg) ? true : seg === p[i]))
}

export function sanitizeUrl(href: string, patterns: readonly string[]): string {
  let pathname: string
  try {
    pathname = new URL(href, 'https://placeholder.invalid').pathname
  } catch {
    return '/unclassified'
  }
  // Most SPECIFIC match wins, measured by literal segment count — NOT string
  // length. Sorting by length makes '/concierge/:publicId' outrank
  // '/concierge/apply', mapping a recordable product route onto an operator
  // pattern. Segment arity already separates '/admin/stories/:id' from
  // '/stories/:id'.
  const literals = (pattern: string) =>
    pattern.split('/').filter((s) => s && !DYNAMIC.test(s)).length
  const hit = patterns
    .filter((pattern) => matches(pathname, pattern))
    .sort((a, b) => literals(b) - literals(a) || a.localeCompare(b))[0]
  return hit ?? '/unclassified'
}

const URL_KEYS = new Set(['$current_url', '$pathname', '$referrer', '$initial_current_url'])

export function sanitizeProperties(
  props: Record<string, unknown>,
  patterns: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(props)) {
    out[k] = URL_KEYS.has(k) && typeof v === 'string' ? sanitizeUrl(v, patterns) : v
  }
  return out
}

export function bucketValue(edges: readonly number[], raw: number): number {
  let band = edges[0]
  for (const edge of edges) if (raw >= edge) band = edge
  return band
}
